import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import { toast } from "sonner";
import api from "../lib/api";

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

const LS_KEY = "stack_cart_v1";

export function CartProvider({ children }) {
  const [cart, setCart] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      return raw ? JSON.parse(raw) : { shop: null, items: {} };
    } catch {
      return { shop: null, items: {} };
    }
  });

  useEffect(() => {
    localStorage.setItem(LS_KEY, JSON.stringify(cart));
  }, [cart]);

  const [fees, setFees] = useState({ delivery_fee: 25, free_delivery_min: 199 });
  useEffect(() => {
    api.get("/settings").then((r) => setFees(r.data)).catch((e) => console.error("Settings load failed:", e));
  }, []);

  const addItem = useCallback((product, shop) => {
    setCart((prev) => {
      if (prev.shop && prev.shop.shop_id !== shop.shop_id) {
        toast.message("Switching shop", {
          description: `Your cart from "${prev.shop.name}" was cleared. Added from ${shop.name}.`,
        });
        return { shop, items: { [product.product_id]: { ...product, qty: 1 } } };
      }
      const existing = prev.items[product.product_id];
      const items = {
        ...prev.items,
        [product.product_id]: { ...product, qty: existing ? existing.qty + 1 : 1 },
      };
      return { shop, items };
    });
  }, []);

  const decrement = useCallback((productId) => {
    setCart((prev) => {
      const existing = prev.items[productId];
      if (!existing) return prev;
      const newQty = existing.qty - 1;
      const items = { ...prev.items };
      if (newQty <= 0) delete items[productId];
      else items[productId] = { ...existing, qty: newQty };
      const shop = Object.keys(items).length === 0 ? null : prev.shop;
      return { shop, items };
    });
  }, []);

  const removeItem = useCallback((productId) => {
    setCart((prev) => {
      const items = { ...prev.items };
      delete items[productId];
      const shop = Object.keys(items).length === 0 ? null : prev.shop;
      return { shop, items };
    });
  }, []);

  const clear = useCallback(() => setCart({ shop: null, items: {} }), []);

  const { itemsArray, count, subtotal, deliveryFee, total } = useMemo(() => {
    const arr = Object.values(cart.items);
    const sub = arr.reduce((s, it) => s + it.price * it.qty, 0);
    const cnt = arr.reduce((s, it) => s + it.qty, 0);
    const fee = sub === 0 ? 0 : sub < fees.free_delivery_min ? fees.delivery_fee : 0;
    return { itemsArray: arr, count: cnt, subtotal: sub, deliveryFee: fee, total: sub + fee };
  }, [cart, fees]);

  const value = useMemo(
    () => ({ cart, itemsArray, count, subtotal, deliveryFee, total, fees, addItem, decrement, removeItem, clear }),
    [cart, itemsArray, count, subtotal, deliveryFee, total, fees, addItem, decrement, removeItem, clear]
  );

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}
